import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { useFrame } from '@/hooks/use-frame'
import { audioEngine } from '@/lib/audio/engine'

interface LevelBarsProps {
    active: boolean
    className?: string
}

const BAR_COUNT = 6
const BINS = [1, 3, 5, 8, 11, 15]

// Monochrome level meter driven by the real audio output while a track plays.
const LevelBars: FC<LevelBarsProps> = ({ active, className }): ReactNode => {
    const bars = useRef<Array<HTMLSpanElement | null>>([])
    const data = useRef<Uint8Array<ArrayBuffer>>(new Uint8Array(32))
    const levels = useRef<number[]>(Array.from({ length: BAR_COUNT }, () => 0.15))

    useFrame(() => {
        audioEngine.readLevels(data.current)

        BINS.forEach((bin, index) => {
            const target = Math.max(0.12, Math.min(1, (data.current[bin] ?? 0) / 210))
            const current = levels.current[index]
            levels.current[index] = current + (target - current) * 0.35

            const bar = bars.current[index]
            if (bar) bar.style.transform = `scaleY(${levels.current[index].toFixed(3)})`
        })
    }, active)

    return (
        <span aria-hidden='true' className={cn('flex h-6 items-end gap-[3px]', className)}>
            {Array.from({ length: BAR_COUNT }, (_, index) => (
                <span
                    key={index}
                    ref={element => {
                        bars.current[index] = element
                    }}
                    className={cn('block h-full w-[4px] origin-bottom transition-colors', active ? 'bg-bandana' : 'bg-current opacity-30')}
                    style={{ transform: 'scaleY(0.15)' }}
                />
            ))}
        </span>
    )
}

export default LevelBars
